import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  Grid,
  IconButton,
  MenuItem,
  Snackbar,
  TextField,
  Typography,
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import { Block, Refresh } from '@mui/icons-material';
import { getRMBNegotiationApprovals, revokeRMBNegotiationApproval } from '../../../services/api';
import CustomPagination from '../../../components/common/CustomPagination';

export default function Negotiations() {
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const [customRowsInput, setCustomRowsInput] = useState('');
  const [customRowsActive, setCustomRowsActive] = useState(false);
  const [status, setStatus] = useState('');
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState({ open: false, text: '', severity: 'success' });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const response = await getRMBNegotiationApprovals({
        page: page + 1,
        page_size: rowsPerPage,
        ...(status ? { status } : {}),
        ...(search.trim() ? { search: search.trim() } : {}),
      });
      const results = response.results || response;
      setRows(Array.isArray(results) ? results : []);
      setTotal(response.count ?? response.total ?? results.length ?? 0);
    } catch (error) {
      setMessage({ open: true, text: error.response?.data?.detail || 'Unable to load negotiation approvals.', severity: 'error' });
    } finally {
      setLoading(false);
    }
  }, [page, rowsPerPage, search, status]);

  useEffect(() => { load(); }, [load]);

  const revoke = async (row) => {
    if (!window.confirm(`Revoke the active approval for ${row.user?.username || 'this customer'}?`)) return;
    try {
      await revokeRMBNegotiationApproval(row.sid);
      setMessage({ open: true, text: 'Negotiation approval revoked.', severity: 'success' });
      load();
    } catch (error) {
      setMessage({ open: true, text: error.response?.data?.detail || 'Unable to revoke approval.', severity: 'error' });
    }
  };

  const totalPages = Math.ceil(total / rowsPerPage) || 1;

  const handlePageChange = (nextPage) => {
    if (nextPage >= 0 && nextPage < totalPages) setPage(nextPage);
  };

  const handleRowsPerPageChange = (event) => {
    const value = event.target.value;
    if (value === 'custom') {
      setCustomRowsActive(true);
      setCustomRowsInput(String(rowsPerPage));
      return;
    }
    setRowsPerPage(Number(value));
    setPage(0);
    setCustomRowsActive(false);
  };

  const handleCustomRowsInputChange = (event) => {
    setCustomRowsInput(event.target.value.replace(/\D/g, ''));
  };

  const handleCustomRowsInputBlur = () => {
    const value = Number.parseInt(customRowsInput, 10);
    if (value > 0) {
      setRowsPerPage(value);
      setPage(0);
    }
    setCustomRowsActive(false);
  };

  const columns = [
    { field: 'customer', headerName: 'Customer', minWidth: 230, flex: 1, valueGetter: (_value, row) => row.user?.fullname || row.user?.username || 'Unknown' },
    { field: 'contact', headerName: 'Phone / Email', minWidth: 220, flex: 1, valueGetter: (_value, row) => row.user?.phone || row.user?.email || '-' },
    { field: 'approved_rmb_amount', headerName: 'Minimum RMB', width: 140, valueFormatter: (value) => `${Number(value || 0).toLocaleString()} RMB` },
    { field: 'status', headerName: 'Status', width: 115, renderCell: ({ value }) => <Chip size="small" label={value} color={value === 'active' ? 'success' : value === 'used' ? 'primary' : 'default'} /> },
    { field: 'approved_by', headerName: 'Approved By', width: 150 },
    {
      field: 'used_transaction',
      headerName: 'Used Transaction / Amount',
      width: 245,
      valueGetter: (_value, row) => row.used_transaction
        ? `${row.used_transaction.transaction_id} / ${Number(row.used_transaction.rmb_amount || 0).toLocaleString()} RMB`
        : '-',
    },
    { field: 'note', headerName: 'Approval Note', minWidth: 220, flex: 1 },
    { field: 'expires_at', headerName: 'Expires', width: 185, valueFormatter: (value) => value ? new Date(value).toLocaleString() : '-' },
    { field: 'created_at', headerName: 'Approved At', width: 185, valueFormatter: (value) => value ? new Date(value).toLocaleString() : '-' },
    { field: 'actions', headerName: '', width: 70, sortable: false, renderCell: ({ row }) => row.status === 'active' ? <IconButton color="error" title="Revoke approval" onClick={() => revoke(row)}><Block /></IconButton> : null },
  ];

  return (
    <Box sx={{ minHeight: '100vh', backgroundColor: 'background.default', px: { xs: 0.75, sm: 1, md: 1.5 }, py: { xs: 2, sm: 2.5, md: 3 }, maxWidth: '95vw', overflowX: 'hidden' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, mb: 3, flexWrap: 'wrap' }}>
        <Box>
          <Typography variant="h4" color="primary" fontWeight={800}>RMB Negotiation History</Typography>
          <Typography color="text.secondary">Review who approved each minimum amount, whether it was used, and the resulting transaction.</Typography>
        </Box>
        <Button variant="outlined" startIcon={<Refresh />} onClick={load}>Refresh</Button>
      </Box>
      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid size={{ xs: 12, md: 8 }}>
          <TextField fullWidth size="small" label="Search customer or note" value={search} onChange={(event) => { setSearch(event.target.value); setPage(0); }} />
        </Grid>
        <Grid size={{ xs: 12, md: 4 }}>
          <TextField fullWidth select size="small" label="Status" value={status} onChange={(event) => { setStatus(event.target.value); setPage(0); }}>
            <MenuItem value="">All</MenuItem>
            <MenuItem value="active">Active</MenuItem>
            <MenuItem value="used">Used</MenuItem>
            <MenuItem value="revoked">Revoked</MenuItem>
            <MenuItem value="expired">Expired</MenuItem>
          </TextField>
        </Grid>
      </Grid>
      <Grid container spacing={2}>
        <Grid size={12}>
          <Box sx={{ width: '100%', overflowX: 'auto', backgroundColor: 'background.paper', borderRadius: 1 }}>
            <DataGrid
              rows={rows}
              columns={columns}
              getRowId={(row) => row.sid}
              loading={loading}
              pageSize={rowsPerPage}
              hideFooter
              autoHeight
              disableRowSelectionOnClick
              sx={{
                minWidth: 1250,
                border: 'none',
                '& .MuiDataGrid-columnHeader': {
                  backgroundColor: (theme) => theme.palette.primary.main,
                },
                '& .MuiDataGrid-columnHeaderTitle': {
                  color: 'white',
                  fontWeight: 'bold',
                  fontSize: { xs: '0.875rem', sm: '1rem' },
                },
                '& .MuiDataGrid-columnHeader .MuiSvgIcon-root': { color: 'white' },
                '& .MuiDataGrid-row:hover': { backgroundColor: '#FFF0F3' },
                '& .MuiDataGrid-cell': {
                  fontSize: { xs: '0.8rem', sm: '0.875rem' },
                  px: { xs: 0.5, sm: 1.5 },
                },
              }}
            />
          </Box>
        </Grid>
        <Grid size={12}>
          <CustomPagination
            page={page}
            totalPages={totalPages}
            rowsPerPage={rowsPerPage}
            onPageChange={handlePageChange}
            onRowsPerPageChange={handleRowsPerPageChange}
            customRowsActive={customRowsActive}
            customRowsInput={customRowsInput}
            onCustomRowsInputChange={handleCustomRowsInputChange}
            onCustomRowsInputBlur={handleCustomRowsInputBlur}
          />
        </Grid>
      </Grid>
      <Snackbar open={message.open} autoHideDuration={4000} onClose={() => setMessage((current) => ({ ...current, open: false }))}>
        <Alert severity={message.severity} onClose={() => setMessage((current) => ({ ...current, open: false }))}>{message.text}</Alert>
      </Snackbar>
    </Box>
  );
}
